import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useChatConfig } from '@/contexts/ChatConfigProvider';
import { aiCoreApi } from '@/services/aiCoreClient';
import { useBoundStore } from '@/store/store';

import { useUser } from './useUser.js';

/**
 * This hook handles the verification of users to make sure, their
 * IDs match the ones associated with the chat. If not, we will
 * logout the user.
 */
export function useAuthVerification() {
  const { logout, isAuthenticated } = useUser();
  const { chatId } = useChatConfig();
  const { opened } = useBoundStore(
    useShallow(state => ({
      opened: state.opened,
    })),
  );

  /**
   * Use useRef to track if verification was already attempted
   * across multiple renders.
   */
  const attemptedVerification = useRef(false);

  /**
   * Verify the chat user. This will verify that the user id
   * still exists in the database. If not we will "logout" the user
   * to ensure that he has to be logged in again.
   */
  const { mutateAsync: verify } = useMutation({
    mutationFn: async (chatId: string) => {
      const result = await aiCoreApi.auth.verify(
        { chatId },
        {
          retry: 0,
        },
      );

      return result.valid;
    },
    onSuccess: (valid: boolean) => {
      /**
       * Logout user if verification failed.
       */
      if (!valid) {
        console.error('User verification failed, logging out');
        logout();
      }
    },
    onError: () => {
      // Logout user if verification failed
      logout();
    },
  });

  // Verify user on mount & chat open
  useEffect(() => {
    // Skip verification if it was already attempted
    if (attemptedVerification.current) {
      return;
    }

    // Verify only if widget is open
    if (!opened) {
      return;
    }

    // If user is not authenticated, we can't verify
    if (!isAuthenticated()) {
      return;
    }

    // Set attempted verification to true
    attemptedVerification.current = true;
    verify(chatId);
  }, [opened, chatId, verify, isAuthenticated]);
}
